import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useBooking } from "@/lib/bookingStore";

const EASE = [0.16, 1, 0.3, 1];

const CHAPTERS = [
    {
        n: "01",
        title: "Discover",
        body: "We interrogate the idea — audience, offer, numbers. If it won't make money, we say so before you spend anything.",
        tags: "// Audience · Offer · Numbers",
    },
    {
        n: "02",
        title: "Design",
        body: "Brand, website and product experience designed as one system — not three vendors pointing at each other.",
        tags: "// Brand · UI/UX · Copy",
    },
    {
        n: "03",
        title: "Build",
        body: "We ship the site and wire the automations underneath it — lead capture, instant follow-ups, client onboarding.",
        tags: "// Development · CRM · Workflows",
    },
    {
        n: "04",
        title: "Launch & Market",
        body: "Campaigns go live. We watch the data, tune the funnel, and keep scaling what works.",
        tags: "// Ads · Funnels · Iteration",
    },
];

export const Process = () => {
    const openBooking = useBooking((s) => s.openBooking);
    return (
    <section
        id="process"
        data-testid="process-section"
        className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32"
    >
        <div className="grid gap-16 md:grid-cols-12">
            <div className="md:col-span-5">
                <div className="md:sticky md:top-32">
                    <motion.div
                        initial={{ opacity: 0, y: 32 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, ease: EASE }}
                    >
                        <p className="font-mono-x mb-6 text-xs uppercase tracking-[0.25em] text-zinc-500">
                            [ The Process ]
                        </p>
                        <h2 className="font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
                            Four moves.
                            <br />
                            <span className="text-outline">Zero chaos.</span>
                        </h2>
                        <p className="mt-8 max-w-sm text-base leading-relaxed text-zinc-400 md:text-lg">
                            A senior four-person team with 12+ years combined across MNC product engineering
                            and growth marketing takes your idea from notebook to
                            revenue.
                        </p>
                        <button
                            data-testid="process-book-call-link"
                            onClick={openBooking}
                            className="group mt-10 inline-flex items-center gap-2 font-mono-x text-xs uppercase tracking-[0.2em] text-white"
                        >
                            <span className="border-b border-white/30 pb-1 transition-colors duration-300 group-hover:border-white">
                                Start with a 30-min call
                            </span>
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
                        </button>
                    </motion.div>
                </div>
            </div>

            <div className="md:col-span-7">
                {CHAPTERS.map((ch, i) => (
                    <motion.div
                        key={ch.n}
                        data-testid={`process-chapter-${ch.n}`}
                        initial={{ opacity: 0, y: 48 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, ease: EASE, delay: i * 0.05 }}
                        className="group border-t border-white/10 py-12 md:py-16"
                    >
                        <span className="font-mono-x text-sm tracking-[0.2em] text-zinc-500 transition-colors duration-300 group-hover:text-white">
                            [{ch.n}]
                        </span>
                        <h3 className="font-display mt-4 text-3xl font-bold tracking-tight text-white md:text-5xl">
                            {ch.title}
                        </h3>
                        <p className="mt-5 max-w-xl text-base leading-relaxed text-zinc-400 md:text-lg">
                            {ch.body}
                        </p>
                        <p className="font-mono-x mt-6 text-[11px] uppercase tracking-[0.2em] text-zinc-600">
                            {ch.tags}
                        </p>
                    </motion.div>
                ))}
                <div className="border-t border-white/10" />
            </div>
        </div>
    </section>
    );
};
